/* eslint-disable */
import React, { useEffect, useState, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { connect } from 'react-redux';
import $ from 'jquery';

import Master from '../Master';
import IziToast from '../IziToast';

const blankUnitRow = () => ({ number: '', service_charge: '' });

// Cap at 2dp — bare toLocaleString() with no options lets binary
// floating-point noise from upstream arithmetic leak straight into the UI.
const moneyOpts = { maximumFractionDigits: 2 };

// ── Small visual helpers ──────────────────────────────────────────────────
const StatCard = ({ label, value, accent }) => (
  <div
    style={{
      flex: 1,
      background: '#fff',
      border: '1px solid #e6e6e6',
      borderLeft: `4px solid ${accent || '#c0392b'}`,
      borderRadius: 6,
      padding: '12px 16px',
      minWidth: 140,
    }}
  >
    <div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5 }}>{label}</div>
    <div style={{ fontSize: 22, fontWeight: 700, color: '#333', marginTop: 4 }}>{value}</div>
  </div>
);
StatCard.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  accent: PropTypes.string,
};
StatCard.defaultProps = { accent: '#c0392b' };

// Compact property tile in the left rail
const PropertyTile = ({ property, isActive, ownerCount, onClick }) => (
  <div
    onClick={onClick}
    style={{
      padding: '10px 12px',
      marginBottom: 6,
      background: isActive ? '#c0392b' : '#fff',
      color: isActive ? '#fff' : '#333',
      borderRadius: 4,
      border: isActive ? '1px solid #c0392b' : '1px solid #e6e6e6',
      cursor: 'pointer',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      transition: 'all 0.15s',
      fontSize: 13,
    }}
    onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = '#f5f5f5'; }}
    onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = '#fff'; }}
  >
    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
      {property.name}
    </span>
    {ownerCount > 0 && (
      <span
        style={{
          background: isActive ? 'rgba(255,255,255,0.25)' : '#f0f0f0',
          color: isActive ? '#fff' : '#666',
          padding: '2px 8px',
          borderRadius: 10,
          fontSize: 11,
          fontWeight: 600,
          marginLeft: 6,
          flexShrink: 0,
        }}
      >
        {ownerCount}
      </span>
    )}
  </div>
);
PropertyTile.propTypes = {
  property: PropTypes.object.isRequired,
  isActive: PropTypes.bool.isRequired,
  ownerCount: PropTypes.number,
  onClick: PropTypes.func.isRequired,
};
PropertyTile.defaultProps = { ownerCount: 0 };

// ── Main component ────────────────────────────────────────────────────────
const UnitOwners = ({ user }) => {
  const userToken = user ? user.token : {};
  const isReadOnly = user && user.type === 'caretaker';
  const requestConfig = {
    headers: { Authorization: `Bearer ${userToken}`, 'Content-Type': 'application/json' },
  };

  const userRef = useRef(null);

  // Page state
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const [users, setUsers] = useState([]);
  const [ownerCountsByProperty, setOwnerCountsByProperty] = useState({});

  // Form state
  const [formOpen, setFormOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [units, setUnits] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [unitTypeId, setUnitTypeId] = useState('');
  const [userId, setUserId] = useState('');
  const [unitRows, setUnitRows] = useState([blankUnitRow()]);
  const [editingUnitOwner, setEditingUnitOwner] = useState(null);

  // Owners table state
  const [owners, setOwners] = useState([]);
  const [isLoadingOwners, setIsLoadingOwners] = useState(false);

  // ── Initial load ────────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const propUrl = user && user.type === 'admin'
          ? `${process.env.REACT_APP_API_URL}properties?take=1000&where={"status":"active"}&order={"name":"ASC"}`
          : `${process.env.REACT_APP_API_URL}properties/byuser?status=active&user_id=${user.id}`;
        const { data: { response } } = await axios.get(propUrl, requestConfig);
        const list = Array.isArray(response) ? response : [response];
        setProperties(list);

        const { data: { response: usersInfo } } = await axios.get(
          `${process.env.REACT_APP_API_URL}users?status=active&type=unit_owner`,
          requestConfig,
        );
        setUsers(usersInfo || []);
      } catch (e) {
        IziToast('Failed to load properties or users');
      }
    })();
  }, []);

  // ── Init the user select2 once users are loaded ─────────────────────────
  useEffect(() => {
    if (!userRef.current || users.length === 0) return;
    const $el = $(userRef.current);
    $el.off('change.unitowners');
    $el.select2({ width: '100%' });
    $el.on('change.unitowners', (event) => setUserId(event.val));
    return () => { $el.off('change.unitowners'); };
  }, [users, formOpen]);

  // ── When property changes, fetch its units + owners ─────────────────────
  useEffect(() => {
    if (!selectedProperty || !selectedProperty.id) {
      setUnits([]);
      setOwners([]);
      return;
    }
    fetchUnits(selectedProperty.id);
    fetchOwners(selectedProperty.id);
  }, [selectedProperty]);

  // ── Tenants for autocomplete on unit number input ───────────────────────
  useEffect(() => {
    if (!unitTypeId) { setTenants([]); return; }
    (async () => {
      try {
        const { data: { response } } = await axios.get(
          `${process.env.REACT_APP_API_URL}tenants?where={"unit_id":"${unitTypeId}"}&take=10000`,
          requestConfig,
        );
        setTenants(response || []);
      } catch (e) { setTenants([]); }
    })();
  }, [unitTypeId]);

  const fetchUnits = async (propertyId) => {
    try {
      const url = `${process.env.REACT_APP_API_URL}units?where={"property_id":"${propertyId}"}&take=10000`;
      const { data: { response } } = await axios.get(url, requestConfig);
      setUnits(response || []);
    } catch (e) { setUnits([]); }
  };

  const fetchOwners = async (propertyId) => {
    setIsLoadingOwners(true);
    try {
      const { data: { response } } = await axios.get(
        `${process.env.REACT_APP_API_URL}unitowners?where={"property_id":"${propertyId}"}`,
        requestConfig,
      );
      setOwners(response || []);
      setOwnerCountsByProperty((prev) => ({ ...prev, [propertyId]: (response || []).length }));
    } catch (e) {
      IziToast('Failed to load unit owners');
      setOwners([]);
    }
    setIsLoadingOwners(false);
  };

  // ── Row helpers ─────────────────────────────────────────────────────────
  // updateUnitRow accepts either (idx, key, value) or (idx, partialObject)
  const updateUnitRow = (idx, keyOrUpdate, value) => {
    const update = typeof keyOrUpdate === 'object'
      ? keyOrUpdate
      : { [keyOrUpdate]: value };
    setUnitRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...update } : r)));
  };
  const addUnitRow = () => setUnitRows((prev) => [...prev, blankUnitRow()]);
  const removeUnitRow = (idx) => {
    setUnitRows((prev) => (prev.length === 1 ? prev : prev.filter((_, i) => i !== idx)));
  };

  // ── Edit ───────────────────────────────────────────────────────────────
  const handleEditUnitOwner = (unitOwner) => {
    setEditingUnitOwner(unitOwner);
    setUnitTypeId(unitOwner.unit_type_id || '');
    setUserId(unitOwner.user_id || '');
    const normalized = Array.isArray(unitOwner.unit_numbers)
      ? unitOwner.unit_numbers.map((u) => (
        typeof u === 'string'
          ? { number: u, service_charge: '' }
          : { number: u.number || '', service_charge: u.service_charge || '' }
      ))
      : [blankUnitRow()];
    setUnitRows(normalized.length > 0 ? normalized : [blankUnitRow()]);
    setFormOpen(true);
    setTimeout(() => {
      if (userRef.current) $(userRef.current).val(unitOwner.user_id || '').trigger('change');
    }, 50);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const resetFields = () => {
    setUnitTypeId('');
    setUserId('');
    setUnitRows([blankUnitRow()]);
    setEditingUnitOwner(null);
    setTenants([]);
    if (userRef.current) $(userRef.current).val('').trigger('change');
  };

  const closeForm = () => {
    resetFields();
    setFormOpen(false);
  };

  // ── Save ───────────────────────────────────────────────────────────────
  const saveUnitOwner = async () => {
    setIsSubmitting(true);
    if (!selectedProperty || !selectedProperty.id) {
      IziToast('Select a property first');
      setIsSubmitting(false);
      return;
    }
    if (!unitTypeId) { IziToast('Select a unit type'); setIsSubmitting(false); return; }
    if (!userId) { IziToast('Select a unit owner'); setIsSubmitting(false); return; }
    const validRows = unitRows
      .map((r) => ({ ...r, number: String(r.number).trim().toUpperCase() }))
      .filter((r) => r.number);
    if (validRows.length === 0) {
      IziToast('Add at least one unit number');
      setIsSubmitting(false);
      return;
    }
    const unit_numbers = validRows.map((r) => ({
      number: r.number,
      service_charge: Number(r.service_charge) || 0,
    }));

    try {
      if (editingUnitOwner) {
        await axios.put(
          `${process.env.REACT_APP_API_URL}unitowners`,
          { id: editingUnitOwner.id, unit_type_id: unitTypeId, unit_numbers },
          requestConfig,
        );
      } else {
        await axios.post(
          `${process.env.REACT_APP_API_URL}unitowners`,
          { property_id: selectedProperty.id, unit_type_id: unitTypeId, user_id: userId, unit_numbers },
          requestConfig,
        );
      }
      IziToast(`Unit owner ${editingUnitOwner ? 'updated' : 'created'}`);
      closeForm();
      fetchOwners(selectedProperty.id);
    } catch (err) {
      const msg = err.response?.data?.error
        || err.response?.data?.info
        || err.message
        || 'An error occurred';
      IziToast(msg);
    }
    setIsSubmitting(false);
  };

  const deleteUnitOwner = async (unitOwner) => {
    if (!window.confirm(`Delete unit owner ${unitOwner.user ? `${unitOwner.user.first_name} ${unitOwner.user.last_name}` : ''}?`)) return;
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}unitowners?id=${unitOwner.id}`,
        requestConfig,
      );
      IziToast('Unit owner deleted');
      fetchOwners(selectedProperty.id);
    } catch (e) {
      IziToast(e.response?.data?.error || 'Failed to delete unit owner');
    }
  };

  // ── Derived ─────────────────────────────────────────────────────────────
  const filteredProperties = useMemo(() => {
    if (!searchValue) return properties;
    return properties.filter((p) => p.name.toLowerCase().includes(searchValue.toLowerCase()));
  }, [properties, searchValue]);

  const existingUnitNumbers = useMemo(() => {
    const list = tenants
      .map((t) => t.other_details && t.other_details.unit_number)
      .filter(Boolean);
    return [...new Set(list)].sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true }));
  }, [tenants]);

  // Stats
  const totalMonthlyCharge = useMemo(() => owners.reduce((sum, o) => {
    if (!Array.isArray(o.unit_numbers)) return sum;
    return sum + o.unit_numbers.reduce((s, u) => {
      const rate = typeof u === 'object' ? Number(u.service_charge) || 0 : 0;
      return s + rate;
    }, 0);
  }, 0), [owners]);

  const totalUnits = useMemo(() => owners.reduce((sum, o) => sum + (Array.isArray(o.unit_numbers) ? o.unit_numbers.length : 0), 0), [owners]);

  return (
    <Master redirectLink="unitowners" title="Property Management" source="Unit Owners">
      <div className="row" style={{ marginTop: 4 }}>

        {/* ── LEFT RAIL ─────────────────────────────────────────────── */}
        <div className="col-md-3">
          <div style={{ position: 'relative', marginBottom: 12 }}>
            <i
              className="fa fa-search"
              style={{
                position: 'absolute', left: 12, top: 11,
                color: '#999', fontSize: 13,
              }}
            />
            <input
              type="text"
              className="form-control"
              placeholder="Search property..."
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              style={{ paddingLeft: 32 }}
            />
          </div>
          <div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5, padding: '4px 2px 8px' }}>
            {filteredProperties.length} {filteredProperties.length === 1 ? 'Property' : 'Properties'}
          </div>
          <div style={{ maxHeight: 'calc(100vh - 260px)', overflowY: 'auto', paddingRight: 4 }}>
            {filteredProperties.map((p) => (
              <PropertyTile
                key={p.id}
                property={p}
                isActive={!!(selectedProperty && selectedProperty.id === p.id)}
                ownerCount={ownerCountsByProperty[p.id] || 0}
                onClick={() => {
                  setSelectedProperty(p);
                  resetFields();
                  setFormOpen(false);
                }}
              />
            ))}
            {filteredProperties.length === 0 && (
              <div style={{ padding: 20, textAlign: 'center', color: '#999', fontSize: 13 }}>
                No properties match
              </div>
            )}
          </div>
        </div>

        {/* ── RIGHT PANEL ───────────────────────────────────────────── */}
        <div className="col-md-9">
          {!selectedProperty && (
            <div
              style={{
                background: '#fafafa', border: '1px dashed #ccc', borderRadius: 8,
                padding: '60px 20px', textAlign: 'center', color: '#888',
              }}
            >
              <i className="fa fa-building-o" style={{ fontSize: 48, color: '#ccc', marginBottom: 12 }} />
              <h4 style={{ color: '#888', margin: 0 }}>Select a property</h4>
              <p style={{ marginTop: 8, fontSize: 13 }}>
                Pick a property from the left to view and manage its unit owners.
              </p>
            </div>
          )}

          {selectedProperty && (
            <>
              {/* ── Property header card ─────────────────────────── */}
              <div
                style={{
                  background: '#fff', border: '1px solid #e6e6e6',
                  borderRadius: 8, padding: '16px 20px', marginBottom: 16,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
                  <div>
                    <h3 style={{ margin: 0, color: '#333' }}>{selectedProperty.name}</h3>
                    <div style={{ color: '#888', fontSize: 13, marginTop: 4 }}>
                      {selectedProperty.property_code && (
                        <span style={{ marginRight: 12 }}>
                          <i className="fa fa-hashtag" style={{ marginRight: 4 }} />
                          {selectedProperty.property_code}
                        </span>
                      )}
                      {selectedProperty.address && (
                        <span>
                          <i className="fa fa-map-marker" style={{ marginRight: 4 }} />
                          {selectedProperty.address}
                        </span>
                      )}
                    </div>
                  </div>
                  {!formOpen && !isReadOnly && (
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={() => { setFormOpen(true); resetFields(); }}
                    >
                      <i className="fa fa-plus" style={{ marginRight: 6 }} />
                      Add Unit Owner
                    </button>
                  )}
                </div>

                <div style={{ display: 'flex', gap: 12, marginTop: 16, flexWrap: 'wrap' }}>
                  <StatCard label="Unit Owners" value={owners.length} accent="#c0392b" />
                  <StatCard label="Units Owned" value={totalUnits} accent="#27ae60" />
                  <StatCard label="Monthly Service Charge" value={`KES ${totalMonthlyCharge.toLocaleString(undefined, moneyOpts)}`} accent="#2980b9" />
                </div>
              </div>

              {/* ── Form panel (collapsible) ─────────────────────── */}
              {formOpen && !isReadOnly && (
                <div
                  style={{
                    background: '#fff', border: '1px solid #e6e6e6',
                    borderRadius: 8, marginBottom: 16, overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      background: '#c0392b', color: '#fff',
                      padding: '12px 20px', display: 'flex',
                      justifyContent: 'space-between', alignItems: 'center',
                    }}
                  >
                    <h4 style={{ margin: 0, color: '#fff' }}>
                      <i className="fa fa-user-plus" style={{ marginRight: 8 }} />
                      {editingUnitOwner ? 'Edit Unit Owner' : 'Add New Unit Owner'}
                    </h4>
                    <button
                      type="button"
                      onClick={closeForm}
                      style={{
                        background: 'transparent', border: 'none', color: '#fff',
                        fontSize: 20, cursor: 'pointer', lineHeight: 1,
                      }}
                    >
                      ×
                    </button>
                  </div>

                  <div style={{ padding: 20 }}>
                    <div className="row">
                      <div className="col-md-6 form-group">
                        <label className="control-label">
                          Unit Type <span style={{ color: '#c0392b' }}>*</span>
                        </label>
                        <select
                          className="form-control"
                          value={unitTypeId}
                          onChange={(e) => setUnitTypeId(e.target.value)}
                          disabled={!!editingUnitOwner}
                        >
                          <option value="">Select unit type</option>
                          {units.map((unit) => (
                            <option key={unit.id} value={unit.id}>
                              {unit.type || 'Unknown'}
                              {unit.rent ? ` — Rent KES ${Number(unit.rent).toLocaleString(undefined, moneyOpts)}` : ''}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="col-md-6 form-group">
                        <label className="control-label">
                          Unit Owner (User) <span style={{ color: '#c0392b' }}>*</span>
                        </label>
                        <select
                          className="form-control"
                          value={userId}
                          onChange={(e) => setUserId(e.target.value)}
                          ref={userRef}
                          disabled={!!editingUnitOwner}
                        >
                          <option value="">Select unit owner</option>
                          {users.map((u) => (
                            <option key={u.id} value={u.id}>
                              {`${u.first_name} ${u.last_name} — ${u.email || ''}`}
                            </option>
                          ))}
                        </select>
                        <small style={{ color: '#777', fontSize: 11 }}>
                          <i className="fa fa-info-circle" style={{ marginRight: 4 }} />
                          The user must have <strong>unit_owner</strong> access to this property
                          (Settings → Property Access).
                        </small>
                      </div>
                    </div>

                    <hr style={{ margin: '16px 0' }} />

                    <div style={{ marginBottom: 8 }}>
                      <label className="control-label" style={{ fontSize: 14 }}>
                        Owned Units &amp; Service Charges
                      </label>
                      <small style={{ display: 'block', color: '#888', marginTop: 2 }}>
                        Add each house number this owner owns and the monthly service charge for that unit.
                      </small>
                    </div>

                    {unitRows.map((row, idx) => {
                      // A row is in custom mode when:
                      //  - the user explicitly asked for custom input, OR
                      //  - there are no tenant unit_numbers to choose from, OR
                      //  - the row already has a number that isn't in the suggestions
                      //    (e.g. legacy data or a vacant unit added earlier)
                      const isCustom = !!row.custom
                        || existingUnitNumbers.length === 0
                        || (row.number && !existingUnitNumbers.includes(row.number));

                      return (
                        <div
                          key={idx}
                          style={{
                            display: 'flex', gap: 8, alignItems: 'center',
                            marginBottom: 8, padding: '8px 12px',
                            background: '#fafafa', borderRadius: 4,
                            border: '1px solid #eee',
                          }}
                        >
                          <div
                            style={{
                              background: '#c0392b', color: '#fff',
                              width: 24, height: 24, borderRadius: '50%',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: 11, fontWeight: 700, flexShrink: 0,
                            }}
                          >
                            {idx + 1}
                          </div>

                          <div style={{ flex: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {isCustom ? (
                              <>
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="House No. (e.g. A1, G4)"
                                  value={row.number}
                                  onChange={(e) => updateUnitRow(idx, { number: e.target.value })}
                                  autoFocus={!!row.custom}
                                />
                                {existingUnitNumbers.length > 0 && (
                                  <button
                                    type="button"
                                    onClick={() => updateUnitRow(idx, { custom: false, number: '' })}
                                    style={{
                                      background: 'transparent', border: 'none', padding: 0,
                                      color: '#c0392b', fontSize: 11, textAlign: 'left',
                                      cursor: 'pointer',
                                    }}
                                  >
                                    <i className="fa fa-arrow-left" /> Back to list
                                  </button>
                                )}
                              </>
                            ) : (
                              <select
                                className="form-control"
                                value={row.number}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  if (v === '__custom__') {
                                    updateUnitRow(idx, { custom: true, number: '' });
                                  } else {
                                    updateUnitRow(idx, { number: v });
                                  }
                                }}
                              >
                                <option value="">Select house number...</option>
                                {existingUnitNumbers.map((n) => (
                                  <option key={n} value={n}>{n}</option>
                                ))}
                                <option value="__custom__">+ Type custom number (vacant unit)...</option>
                              </select>
                            )}
                          </div>

                          <div style={{ position: 'relative', flex: 2 }}>
                            <span
                              style={{
                                position: 'absolute', left: 12, top: 9,
                                color: '#999', fontSize: 12, fontWeight: 600,
                              }}
                            >
                              KES
                            </span>
                            <input
                              type="number"
                              min="0"
                              step="any"
                              className="form-control"
                              placeholder="Service charge"
                              value={row.service_charge}
                              onChange={(e) => updateUnitRow(idx, { service_charge: e.target.value })}
                              style={{ paddingLeft: 42 }}
                            />
                          </div>
                          <button
                            type="button"
                            className="btn btn-default btn-sm"
                            onClick={() => removeUnitRow(idx)}
                            disabled={unitRows.length === 1}
                            title="Remove this unit"
                            style={{ flexShrink: 0 }}
                          >
                            <i className="fa fa-trash" />
                          </button>
                        </div>
                      );
                    })}

                    <button
                      type="button"
                      className="btn btn-default btn-sm"
                      onClick={addUnitRow}
                      style={{ marginTop: 4 }}
                    >
                      <i className="fa fa-plus" style={{ marginRight: 4 }} />
                      Add another unit
                    </button>

                    <div style={{ textAlign: 'right', marginTop: 20, paddingTop: 16, borderTop: '1px solid #eee' }}>
                      <button
                        type="button"
                        className="btn btn-default"
                        onClick={closeForm}
                        style={{ marginRight: 8 }}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="btn btn-danger"
                        onClick={saveUnitOwner}
                        disabled={isSubmitting}
                      >
                        {isSubmitting
                          ? 'Saving...'
                          : editingUnitOwner ? 'Update Unit Owner' : 'Create Unit Owner'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* ── Owners table or empty state ──────────────────── */}
              {isLoadingOwners && (
                <div style={{ padding: 40, textAlign: 'center', color: '#999' }}>
                  <i className="fa fa-spinner fa-spin" style={{ fontSize: 20, marginRight: 8 }} />
                  Loading unit owners...
                </div>
              )}

              {!isLoadingOwners && owners.length === 0 && (
                <div
                  style={{
                    background: '#fafafa', border: '1px dashed #ccc',
                    borderRadius: 8, padding: '50px 20px', textAlign: 'center', color: '#888',
                  }}
                >
                  <i className="fa fa-users" style={{ fontSize: 40, color: '#ccc', marginBottom: 10 }} />
                  <h4 style={{ color: '#888', margin: 0 }}>No unit owners yet</h4>
                  <p style={{ marginTop: 8, fontSize: 13 }}>
                    {isReadOnly
                      ? 'No unit owners have been configured for this property.'
                      : formOpen
                        ? 'Fill in the form above to add the first unit owner.'
                        : 'Click "Add Unit Owner" above to add the first one.'}
                  </p>
                </div>
              )}

              {!isLoadingOwners && owners.length > 0 && (
                <div style={{ background: '#fff', border: '1px solid #e6e6e6', borderRadius: 8, overflow: 'hidden' }}>
                  <div style={{ padding: '12px 20px', borderBottom: '1px solid #e6e6e6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h4 style={{ margin: 0 }}>Unit Owners</h4>
                    <span style={{ fontSize: 12, color: '#888' }}>{owners.length} record(s)</span>
                  </div>
                  <div style={{ overflowX: 'auto' }}>
                    <table className="table" style={{ margin: 0 }}>
                      <thead style={{ background: '#fafafa' }}>
                        <tr>
                          <th style={{ width: 50 }}>#</th>
                          <th>Owner</th>
                          <th>Unit Type</th>
                          <th>Units &amp; Service Charge</th>
                          <th style={{ textAlign: 'right' }}>Monthly Total</th>
                          {!isReadOnly && (
                            <th style={{ width: 110, textAlign: 'right' }}>Actions</th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {owners.map((o, idx) => {
                          const ownerName = o.user
                            ? `${o.user.first_name} ${o.user.last_name}`
                            : '-';
                          const rows = Array.isArray(o.unit_numbers) ? o.unit_numbers : [];
                          const monthlyTotal = rows.reduce((s, u) => {
                            const rate = typeof u === 'object' ? Number(u.service_charge) || 0 : 0;
                            return s + rate;
                          }, 0);
                          return (
                            <tr key={o.id}>
                              <td>{idx + 1}</td>
                              <td>
                                <div style={{ fontWeight: 600 }}>{ownerName}</div>
                                {o.user && (
                                  <small style={{ color: '#888' }}>
                                    {o.user.email}
                                    {o.user.phone && <span> · {o.user.phone}</span>}
                                  </small>
                                )}
                              </td>
                              <td>{(o.unit && o.unit.type) || '-'}</td>
                              <td>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                  {rows.length === 0 && '-'}
                                  {rows.map((u, i) => {
                                    const num = typeof u === 'string' ? u : u.number;
                                    const rate = typeof u === 'string' ? 0 : Number(u.service_charge) || 0;
                                    return (
                                      <span
                                        key={`${num}-${i}`}
                                        style={{
                                          background: '#f0f0f0', borderRadius: 12,
                                          padding: '2px 10px', fontSize: 12,
                                          display: 'inline-flex', alignItems: 'center', gap: 6,
                                        }}
                                      >
                                        <strong>{num}</strong>
                                        {rate > 0 && (
                                          <span style={{ color: '#666' }}>
                                            KES {rate.toLocaleString(undefined, moneyOpts)}
                                          </span>
                                        )}
                                      </span>
                                    );
                                  })}
                                </div>
                              </td>
                              <td style={{ textAlign: 'right', fontWeight: 600, color: '#2980b9' }}>
                                {monthlyTotal > 0 ? `KES ${monthlyTotal.toLocaleString(undefined, moneyOpts)}` : '-'}
                              </td>
                              {!isReadOnly && (
                                <td style={{ textAlign: 'right' }}>
                                  <button
                                    type="button"
                                    className="btn btn-default btn-sm"
                                    onClick={() => handleEditUnitOwner(o)}
                                    style={{ marginRight: 4 }}
                                    title="Edit"
                                  >
                                    <i className="fa fa-pencil" />
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-danger btn-sm"
                                    onClick={() => deleteUnitOwner(o)}
                                    title="Delete"
                                  >
                                    <i className="fa fa-trash" />
                                  </button>
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Master>
  );
};

const mapStateToProps = (state) => state;
UnitOwners.propTypes = { user: PropTypes.oneOfType([PropTypes.object, PropTypes.string]) };
UnitOwners.defaultProps = { user: {} };
export default connect(mapStateToProps)(UnitOwners);
